import { Button, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { CIMSModule, CIMSPage, pathify } from 'shared'
import { useAuthContext } from 'context/AuthContext'
import { useGlobalState } from 'context/GlobalStateContext'
import { useRouteMatches } from 'hooks/useRouteMatches'
import { cimsStorage } from 'utils/storage'

/**
 * this page is rendered when you first come to the site, regardless of what the
 * url is due to the ProtectedRoute component's auth guard in its render prop
 *
 * the following use effect should handle all edge cases we care about for that
 * initial render
 *
 * most of the time, you'll be redirected to where you should go, unless the server
 * isn't up, in which case you can use the button to trigger the flow again
 *
 * there is some interplay between a couple components you should be aware of
 * 1. the Routes component should handle and redirect deprecated paths to their
 *    current ones, which will likely send users here if they aren't authenticated
 * 2. the SideNav component should handle manually entered urls while in the
 *    application context and navigation by page selection
 * 3. the ProtectedRoute component should handle disallowed navigation and help
 *    unauthenticated users get here, making use of a `from` state variable in the
 *    router state so we can send them back to where they wanted to go in the first
 *    place
 */
const Login: React.FC = () => {
  /** see ProtectedRoute for why we're using `from` here */
  const { state } = useLocation<{ from?: string }>()
  const { user } = useAuthContext()
  const { replace } = useHistory()
  const { dispatch: d } = useGlobalState()
  const [from, setFrom] = useState<string>()
  const { param } = useRouteMatches(from)
  useEffect(() => {
    if (state?.from) setFrom(state.from)
  }, [state])

  useEffect(() => {
    // if there's no user, we still need to log in and will keep coming here until we're done
    if (!user) return

    // build out each path segment we care about
    const segments: string[] = []

    // pull project id out of address bar or storage
    const projectId = param('projectId')
    if (projectId || cimsStorage.projectId) {
      if (projectId && projectId !== cimsStorage.projectId)
        d.updateProjectGlobalId(projectId)
      if (projectId) segments.push(projectId)
      else if (cimsStorage.projectId) segments.push(cimsStorage.projectId)
    }

    // pull current page information out of address bar or storage
    const page =
      CIMSPage.fromPath({
        module: param('module'),
        page: param('page'),
      }) ?? CIMSPage.fromId(cimsStorage.pageId)
    if (page) {
      setFrom(undefined)
      d.updateNavKey(page.id)
      segments.push(page.module.pathname, page.pathname)
      const deepLink = param('deepLink')
      if (deepLink) segments.push(deepLink)
    } else {
      const { id, pathname } = CIMSModule.Home
      d.updateNavKey(id)
      segments.push(pathname)
    }

    return replace(pathify(...segments))
  }, [user, replace, param, d])

  return (
    <>
      {!user && (
        <Typography.Paragraph>
          Please log in to use this application
        </Typography.Paragraph>
      )}
      {user && (
        <Typography.Paragraph>
          For security reasons, user sessions expire over time. Please log in
          again to refresh it.
        </Typography.Paragraph>
      )}
      <Button type="primary" href={process.env.REACT_APP_LOGIN_URL}>
        Log In
      </Button>
    </>
  )
}

export default Login
